import React, { useEffect, useMemo, useRef, useState } from "react";
import { ClipLoader } from "react-spinners";
import { BiSolidError, BiSolidCheckCircle } from "react-icons/bi";
import { validateFilesSnow, isHeicLike, ensureJpegIfHeic } from "../utils/FileHelper";

/**
 * Props:
 * - maxFiles?: number (optional; unlimited if not provided)
 * - validateInSnow?: boolean (default true)
 * - accept?: string (default ".png, .pdf, .heic")
 * - disabled?: boolean
 * - onFilesChange?: (files: File[], rows: Row[]) => void
 */
export default function AttachmentPicker({
  maxFiles,
  validateInSnow = true,
  accept = ".png, .pdf, .heic",
  disabled = false,
  onFilesChange,
}) {
  const inputRef = useRef(null);

  const tokenRef = useRef(0);

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
    if (typeof onFilesChange === "function") {
      const okFiles = rows
        .filter((r) => r.status === "success" && r.file)
        .map((r) => r.file);
      onFilesChange(okFiles, rows);
    }
  }, [rows, onFilesChange]);
  

  const anyBusy = useMemo(
    () => rows.some((r) => r.status === "converting" || r.status === "loading"),
    [rows]
  );

  const atMax = useMemo(() => {
    if (typeof maxFiles !== "number" || maxFiles <= 0) return false;
    return rows.length >= maxFiles;
  }, [rows.length, maxFiles]);

  const openPicker = () => {
    if (disabled) return;
    if (atMax) return;
    inputRef.current?.click();
  };

  const bumpToken = () => {
    tokenRef.current += 1;
    return tokenRef.current;
  };

  const validateSingleAttachmentSnow = async (file) => {
    try {
      const res = await validateFilesSnow([file]);
      const results = res?.Result?.result || [];
      const r = Array.isArray(results) ? results[0] : null;

      if (!r) return { ok: false, message: "Validation failed: empty response." };
      if (r?.Status === "Success") return { ok: true, message: "" };

      return { ok: false, message: r?.Message || "Invalid file." };
    } catch (e) {
      return { ok: false, message: "Validation request failed." };
    }
  };

  const removeAt = (rowId) => {
    bumpToken();

    setRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const processAddedFiles = async (fileList) => {
    const token = bumpToken();
    const incoming = Array.from(fileList || []);

    if (incoming.length === 0) return;

    const currentCount = rowsRef.current.length;
    const remainingSlots =
      typeof maxFiles === "number" && maxFiles > 0 ? Math.max(0, maxFiles - currentCount) : Infinity;

    const accepted = incoming.slice(0, remainingSlots);
    const rejected = incoming.slice(remainingSlots);

    const newRows = accepted.map((f) => ({
      id: makeId(),
      file: f,
      originalName: f.name,
      status: isHeicLike(f) ? "converting" : validateInSnow ? "loading" : "loading",
      message: "",
    }));

    const rejectedRows = rejected.map((f) => ({
      id: makeId(),
      file: null,
      originalName: f.name,
      status: "failure",
      message:
        typeof maxFiles === "number" && maxFiles > 0
          ? `Maximum of ${maxFiles} file(s) allowed.`
          : "File not accepted.",
    }));

    const startIndex = rowsRef.current.length;
    setRows((prev) => [...prev, ...newRows, ...rejectedRows]);

    for (let i = 0; i < accepted.length; i++) {
      if (token !== tokenRef.current) return; // stale
      const raw = accepted[i];
      const rowIndex = startIndex + i;

      try {
        let finalFile = raw;

        if (isHeicLike(raw)) {
          setRows((prev) =>
            prev.map((r, idx) => (idx === rowIndex ? { ...r, status: "converting", message: "" } : r))
          );

          if (token !== tokenRef.current) return;
          finalFile = await ensureJpegIfHeic(raw);
        }

        if (token !== tokenRef.current) return;

        setRows((prev) =>
          prev.map((r, idx) =>
            idx === rowIndex
              ? { ...r, file: finalFile, status: "loading", message: "" }
              : r
          )
        );

        await new Promise((res) => requestAnimationFrame(res));

        if (token !== tokenRef.current) return;

        if (!validateInSnow) {
          setRows((prev) =>
            prev.map((r, idx) =>
              idx === rowIndex
                ? { ...r, file: finalFile, status: "success", message: "" }
                : r
            )
          );
          continue;
        }

        const v = await validateSingleAttachmentSnow(finalFile);

        if (token !== tokenRef.current) return;

        setRows((prev) =>
          prev.map((r, idx) =>
            idx === rowIndex
              ? {
                  ...r,
                  file: finalFile,
                  status: v.ok ? "success" : "failure",
                  message: v.message,
                }
              : r
          )
        );
      } catch (err) {
        if (token !== tokenRef.current) return;

        let msg = "Failed to process attachment.";
        if (err?.message?.includes("Image is already browser readable")) {
          msg =
            "The file content doesn’t match its extension. It may have been renamed or corrupted. Please upload a valid file.";
        } else if (err?.message) {
          msg = err.message;
        } else if (typeof err === "string") {
          msg = err;
        }

        setRows((prev) =>
          prev.map((r, idx) => (idx === rowIndex ? { ...r, status: "failure", message: msg } : r))
        );
      }
    }
  };

  const clearAll = () => {
    bumpToken();
    setRows([]);
  };

  const statusLine = (row) => {
    const name = row.file?.name || row.originalName || "File";

    if (row.status === "converting") {
      return (
        <>
          <ClipLoader size={16} color="#000000" />
          <span style={{ color: "#000" }}>Converting... {name}</span>
        </>
      );
    }

    if (row.status === "loading") {
      return (
        <>
          <ClipLoader size={16} color="#000000" />
          <span style={{ color: "#000" }}>
            {validateInSnow ? "Validating..." : "Processing..."} {name}
          </span>
        </>
      );
    }

    if (row.status === "success") {
      return (
        <>
          <BiSolidCheckCircle size={18} color="#2e7d32" />
          <span style={{ color: "#2e7d32", fontWeight: 500 }}>{name} OK</span>
        </>
      );
    }

    if (row.status === "failure") {
      return (
        <>
          <BiSolidError size={18} color="#d32f2f" />
          <span style={{ color: "#d32f2f", fontWeight: 500, whiteSpace: "pre-line" }}>
            {name}: {row.message || "Invalid file."}
          </span>
        </>
      );
    }

    return <span style={{ color: "#666" }}>{name}</span>;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const files = e.target.files;
          processAddedFiles(files);

          if (inputRef.current) inputRef.current.value = "";
        }}
        disabled={disabled}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled || atMax}
          className="red-button"
          style={{
            padding: "6px 10px",
            cursor: disabled || atMax ? "not-allowed" : "pointer",
          }}
        >
         <b> Add {rows.length > 0 ? "more" : "files"}</b>
        </button>

        <button
          type="button"
          className="red-button"
          onClick={clearAll}
          disabled={disabled || rows.length === 0}
          style={{
            padding: "6px 10px",
            backgroundColor: "#000",
            cursor: disabled || rows.length === 0 ? "not-allowed" : "pointer",
          }}
        >
      <b></b>    Remove all
        </button>

        <div style={{ color: "#666", fontSize: 12 }}>
          {typeof maxFiles === "number" && maxFiles > 0
            ? `${rows.length}/${maxFiles} selected`
            : `${rows.length} selected`}
          {anyBusy ? " • Working..." : ""}
          {!validateInSnow ? " • No ServiceNow validation" : ""}
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flexWrap: "wrap", alignContent: "start" }}>
          {rows.map((row) => (
            <div
              key={row.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                justifyContent: "space-between",
                padding: "6px 8px",
                
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexDirection: "row" }}>
                {statusLine(row)}
              </div>

              <button
                type="button"
                className="red-button"
                onClick={() => removeAt(row.id)}
                disabled={disabled || row.status === "converting" || row.status === "loading"}
                style={{
                  padding: "4px 8px",
                  background: "#000",
                  cursor:
                    disabled || row.status === "converting" || row.status === "loading"
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 12,
                  width: "100px"
                }}
                title={
                  row.status === "converting" || row.status === "loading"
                    ? "Wait until processing finishes"
                    : "Remove"
                }
              >
               <b> Remove</b>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}